<template>
    <div>
        <table
            class="min-w-full border border-gray-300 divide-y divide-gray-300 dark:border-neutral-800 dark:divide-neutral-800"
        >
            <thead class="bg-gray-50 dark:bg-neutral-800">
                <tr>
                    <th
                        scope="col"
                        class="py-3.5 px-4 text-left text-xs font-semibold text-gray-900 dark:text-neutral-50"
                    >
                        {{ lang('products::amounts.fields.name') }}
                        <p class="text-xs text-gray-500 dark:text-neutral-400">
                            {{ lang('products::amounts.fields.name_help') }}
                        </p>
                    </th>
                    <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-xs font-semibold text-gray-900 whitespace-nowrap dark:text-neutral-50"
                    >
                        {{ lang('products::amounts.fields.price_rule') }}
                    </th>
                    <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-6"></th>
                </tr>
            </thead>
            <tbody v-if="hasAmounts" class="divide-y divide-gray-200 dark:divide-neutral-800">
                <tr v-for="amount in modelValue" :key="amount.id">
                    <th
                        scope="row"
                        class="w-[30%] py-3.5 px-4 text-left text-sm font-semibold text-gray-900 bg-gray-50 dark:bg-neutral-900 border-r border-gray-200 dark:border-neutral-800 align-top"
                    >
                        <AdminText v-model="amount.name" type="number" />
                    </th>
                    <td class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 align-top whitespace-nowrap">
                        <div class="flex items-center justify-between w-full gap-4">
                            <AdminSelect
                                v-model="amount.modifier_type"
                                size="sm"
                                :options="modifierTypes"
                            ></AdminSelect>
                            <AdminText
                                v-if="amount.modifier_type !== null"
                                v-model="amount.modifier_value"
                                size="xs"
                                @blur="formatModifierValue(amount)"
                            ></AdminText>
                        </div>
                    </td>

                    <td class="w-12 py-3.5 pr-4 sm:pr-6 align-top">
                        <Button @click.prevent="removeAmount(amount)">
                            <span class="flex items-center justify-center size-8">
                                <icon icon="hero/outline/trash" class="size-4"></icon>
                            </span>
                        </Button>
                    </td>
                </tr>
            </tbody>
            <tbody v-else>
                <tr>
                    <td
                        colspan="2"
                        class="py-3.5 space-y-1 text-center text-sm font-semibold text-neutral-900 dark:text-neutral-100"
                    >
                        <p>
                            {{ lang('products::amounts.messages.no_values') }}
                        </p>
                        <p>
                            <Button @click.prevent="addAmount">
                                <span>
                                    {{ lang('products::amounts.actions.add_amount') }}
                                </span>
                                <icon icon="hero/outline/plus" class="size-4"></icon>
                            </Button>
                        </p>
                    </td>
                </tr>
            </tbody>
            <tfoot class="bg-gray-50 dark:bg-neutral-900">
                <tr>
                    <th
                        colspan="2"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-neutral-900 dark:text-neutral-100 sm:pl-6"
                    ></th>
                    <th class="w-12 py-3.5 pr-4 sm:pr-6">
                        <Button @click.prevent="addAmount">
                            <span class="flex items-center justify-center size-8">
                                <icon icon="hero/outline/plus" class="size-4"></icon>
                            </span>
                        </Button>
                    </th>
                </tr>
            </tfoot>
        </table>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { lang } from '@/lang/composable';
import type { FieldComponent } from '@src/Common/Components/types';

const modelValue = defineModel({
    type: [Object, undefined],
    default: () => [],
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const props = withDefaults(defineProps<FieldComponent>(), {});

const addAmount = () => {
    modelValue.value = [
        ...modelValue.value,
        {
            id: null,
            name: 1,
            modifier_type: null,
            modifier_value: 0.0,
        },
    ];
};

const removeAmount = (amount) => {
    modelValue.value = modelValue.value.filter((item) => item !== amount);
};

const hasAmounts = computed(() => modelValue.value.length > 0);

const modifierTypes = [
    {
        id: null,
        name: lang('products::values.fields.no_modifier'),
    },
    {
        id: 'add',
        name: '+',
    },
    {
        id: 'subtract',
        name: '-',
    },
    {
        id: 'multiply',
        name: '*',
    },
    {
        id: 'divide',
        name: '/',
    },
    {
        id: 'equal',
        name: '=',
    },
];

const formatModifierValue = (amount) => {
    if (!isNaN(amount.modifier_value)) {
        amount.modifier_value = parseFloat(amount.modifier_value);
    }
};

onMounted(() => {
    modelValue.value.forEach((amount) => {
        if (amount.modifier_value != null && !isNaN(amount.modifier_value)) {
            amount.modifier_value = parseFloat(amount.modifier_value);
        }
    });
});
</script>
